import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';

import { FetchGetOffersByPinLoadService } from '@/packages/query-state/shared/store/search-offers/v1/get-offers-by-pin/load';
import { useFetchSearchOffersDesktopService } from '@/packages/query-state/shared/store/search-offers/v3/search-offers-desktop/hooks/useFetchSearchOffersDesktopService';
import { useActivePinListingIdSelector } from '@/packages/redux-state/store/slices/mapControls/selectors/dumb';

import { type TGetOffersByPinResponse_1 } from '@/repositories/generated/shared/search-offers/v1/get-offers-by-pin';

import { type TCardApiContext } from '../../types/TCardApiContext';
import { type TCardContextData } from '../../types/TCardContextData';

import { useRefetchClusters } from './internal/hooks/useRefetchClusters';
import { type InitCardApiContext } from './types';

export function useInitListingByPinCardApi(): InitCardApiContext {
  const queryClient = useQueryClient();
  const listingPinId = useActivePinListingIdSelector();
  const fetchGetOffersByPinLoadService = useMemo(() => new FetchGetOffersByPinLoadService(queryClient), [queryClient]);
  const fetchGetOffersLoadService = useFetchSearchOffersDesktopService();

  const refetchClustersOnSuccess = useRefetchClusters();

  return useCallback<(cardContextData: TCardContextData) => TCardApiContext>(
    cardContextData => ({
      hideOfferMutationOptions: {
        onError(_error, _variables, context: TGetOffersByPinResponse_1 | null): void {
          if (context && listingPinId) {
            fetchGetOffersByPinLoadService.setQueryData(context, listingPinId);
          }
        },
        onMutate(): TGetOffersByPinResponse_1 | null {
          if (listingPinId) {
            const prev = fetchGetOffersByPinLoadService.getQueryData(listingPinId);

            if (prev) {
              const next: TGetOffersByPinResponse_1 = JSON.parse(JSON.stringify(prev));
              next.offers.splice(cardContextData.index, 1);

              fetchGetOffersByPinLoadService.setQueryData(next, listingPinId);
            }

            return prev;
          }

          return null;
        },
        onSuccess(): void {
          refetchClustersOnSuccess();
          fetchGetOffersLoadService.removeOfferById(cardContextData.offer.id);
        },
      },
      addToFavoriteMutationOptions: {
        onError: (_error, _variables, context: TGetOffersByPinResponse_1): void => {
          if (context && listingPinId) {
            fetchGetOffersByPinLoadService.setQueryData(context, listingPinId);
          }
        },
        onMutate(): TGetOffersByPinResponse_1 | null {
          if (listingPinId) {
            const prev = fetchGetOffersByPinLoadService.getQueryData(listingPinId);

            if (prev) {
              const next: TGetOffersByPinResponse_1 = JSON.parse(JSON.stringify(prev));
              next.offers[cardContextData.index].data.extendedProperties.isFavorite = true;

              fetchGetOffersByPinLoadService.setQueryData(next, listingPinId);
            }

            return prev;
          }

          return null;
        },
        onSuccess: (): void => {
          refetchClustersOnSuccess();

          /**
           * При работе в листинге по пину, добавив объявление в избранное, мы затем можем вернуться обратно на выдачу.
           * В таком случае мы не запрашиваем данные листинга снова, а используем кэш ReactQuery.
           * А значит, если в основном листинге есть то объявление, с которым мы работали, то оно покажет неправильное
           * значение "isFavorite".
           * Поэтому мы должны поменять флаг "isFavorite" у того оффера, с которым работали, руками и обновить кэш.
           */
          fetchGetOffersLoadService.updateFavoriteByOfferId(cardContextData.offer.id, true);
        },
      },
      deleteFromFavoriteMutationOptions: {
        onError: (_error, _variables, context: TGetOffersByPinResponse_1): void => {
          if (context && listingPinId) {
            fetchGetOffersByPinLoadService.setQueryData(context, listingPinId);
          }
        },
        onMutate(): TGetOffersByPinResponse_1 | null {
          if (listingPinId) {
            const prev = fetchGetOffersByPinLoadService.getQueryData(listingPinId);

            if (prev) {
              const next: TGetOffersByPinResponse_1 = JSON.parse(JSON.stringify(prev));
              next.offers[cardContextData.index].data.extendedProperties.isFavorite = false;

              fetchGetOffersByPinLoadService.setQueryData(next, listingPinId);
            }

            return prev;
          }

          return null;
        },
        onSuccess: (): void => {
          refetchClustersOnSuccess();

          /**
           * При работе в листинге по пину, удалив объявление из избранного, мы затем можем вернуться обратно на выдачу.
           * В таком случае мы не запрашиваем данные листинга снова, а используем кэш ReactQuery.
           * А значит, если в основном листинге есть то объявление, с которым мы работали, то оно покажет неправильное
           * значение "isFavorite".
           * Поэтому мы должны поменять флаг "isFavorite" у того оффера, с которым работали, руками и обновить кэш.
           */
          fetchGetOffersLoadService.updateFavoriteByOfferId(cardContextData.offer.id, false);
        },
      },
      addToComparisonMutationOptions: {
        onError: (_error, _variables, context: TGetOffersByPinResponse_1): void => {
          if (context && listingPinId) {
            fetchGetOffersByPinLoadService.setQueryData(context, listingPinId);
          }
        },
        onMutate(): TGetOffersByPinResponse_1 | null {
          if (listingPinId) {
            const prev = fetchGetOffersByPinLoadService.getQueryData(listingPinId);

            if (prev) {
              const next: TGetOffersByPinResponse_1 = JSON.parse(JSON.stringify(prev));
              next.offers[cardContextData.index].data.comparisonStatus = {
                description: 'Добавлено в сравнение',
                status: 'added',
              };

              fetchGetOffersByPinLoadService.setQueryData(next, listingPinId);
            }

            return prev;
          }

          return null;
        },
        onSuccess: (): void => {
          refetchClustersOnSuccess();

          fetchGetOffersLoadService.updateCompareByOfferId(cardContextData.offer.id, {
            description: 'Добавлено в сравнение',
            status: 'added',
          });
        },
      },
      deleteFromComparisonMutationOptions: {
        onError: (_error, _variables, context: TGetOffersByPinResponse_1): void => {
          if (context && listingPinId) {
            fetchGetOffersByPinLoadService.setQueryData(context, listingPinId);
          }
        },
        onMutate(): TGetOffersByPinResponse_1 | null {
          if (listingPinId) {
            const prev = fetchGetOffersByPinLoadService.getQueryData(listingPinId);

            if (prev) {
              const next: TGetOffersByPinResponse_1 = JSON.parse(JSON.stringify(prev));
              next.offers[cardContextData.index].data.comparisonStatus = {
                description: 'Добавить к сравнению',
                status: 'available',
              };

              fetchGetOffersByPinLoadService.setQueryData(next, listingPinId);
            }

            return prev;
          }

          return null;
        },
        onSuccess: (): void => {
          refetchClustersOnSuccess();

          fetchGetOffersLoadService.updateCompareByOfferId(cardContextData.offer.id, {
            description: 'Добавить к сравнению',
            status: 'available',
          });
        },
      },
    }),
    [fetchGetOffersByPinLoadService, fetchGetOffersLoadService, refetchClustersOnSuccess, listingPinId],
  );
}
